import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ListChecks, Search, X, RefreshCw, AlertTriangle, Loader2, Filter, Package, Clock,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MobileListCard from '../components/MobileListCard';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

// ============================================================
// AdminAllocations — cross-order Allocation Details report.
// Feb 2026 — Renders every allocated pick line across active orders
// with filters for order #, item code, branch, and allocation status
// (full / partial / shortfall). Server-side filters keep the payload
// small; the client only sorts + paginates the resulting rows.
// ============================================================
const AdminAllocations = () => {
  const { authHeaders } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, full: 0, partial: 0, shortfall: 0, orders: 0 });
  const [branches, setBranches] = useState([]);

  // Filter state — kept as plain strings so the URL / inputs stay in
  // lockstep. `applied*` are the values we actually POST to the API;
  // the raw form values only update on user typing.
  const [filterOrder, setFilterOrder] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchAllocations = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterOrder.trim()) params.order_number = filterOrder.trim();
      if (filterItem.trim()) params.item_code = filterItem.trim();
      if (filterBranch && filterBranch !== 'all') params.branch_id = filterBranch;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const { data } = await axios.get(`${API}/admin/orders/allocations`, {
        headers: authHeaders,
        params,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setSummary(data?.summary || { total: 0, full: 0, partial: 0, shortfall: 0, orders: 0 });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load allocation report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load the branch list once for the filter dropdown.
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/branches`, { headers: authHeaders });
        const list = Array.isArray(data) ? data : (data?.branches || []);
        setBranches(list);
      } catch { /* filter still works without branches */ }
    })();
    fetchAllocations();
  }, []);

  const applyFilters = () => fetchAllocations();

  const clearFilters = () => {
    setFilterOrder('');
    setFilterItem('');
    setFilterBranch('all');
    setFilterStatus('all');
    // Trigger a fetch on next tick after state clears so params are empty.
    setTimeout(() => fetchAllocations(), 0);
  };

  // Simple client-side status colour map — mirrors the semantic pill
  // colours used elsewhere (green = full, amber = partial, red = shortfall).
  const statusPill = (s) => {
    if (s === 'shortfall') return 'bg-red-100 text-red-700 border border-red-200';
    if (s === 'partial') return 'bg-amber-100 text-amber-800 border border-amber-200';
    return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
  };

  const hasAnyFilter = filterOrder || filterItem || (filterBranch && filterBranch !== 'all') || (filterStatus && filterStatus !== 'all');

  return (
    <AdminLayout>
      <div className="max-w-[1600px] mx-auto px-4 py-6" data-testid="admin-allocations-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <ListChecks className="w-6 h-6 text-[#0078D4]" />
              <h1 className="text-3xl font-bold text-[#0F172A]">Allocation Details</h1>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Every stock commitment across active orders in one live view.
            </p>
          </div>
          <Button
            onClick={fetchAllocations}
            disabled={loading}
            variant="outline"
            className="rounded-full h-9"
            data-testid="allocations-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {/* KPI chips — one per allocation status + total */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="allocations-summary">
          <SummaryTile label="Total lines" value={summary.total} testid="allocations-summary-total" />
          <SummaryTile label="Orders" value={summary.orders} testid="allocations-summary-orders" />
          <SummaryTile label="Fully allocated" value={summary.full} tone="emerald" testid="allocations-summary-full" />
          <SummaryTile label="Partial" value={summary.partial} tone="amber" testid="allocations-summary-partial" />
          <SummaryTile label="Shortfall" value={summary.shortfall} tone="red" testid="allocations-summary-shortfall" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-neutral-200 p-3 mb-4" data-testid="allocations-filters">
          <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500 uppercase font-bold tracking-wider">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Order #</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={filterOrder}
                  onChange={(e) => setFilterOrder(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  placeholder="MR-00000..."
                  className="pl-8"
                  data-testid="allocations-filter-order"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Item Code</label>
              <div className="relative">
                <Package className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={filterItem}
                  onChange={(e) => setFilterItem(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  placeholder="SKU..."
                  className="pl-8"
                  data-testid="allocations-filter-item"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Branch</label>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger data-testid="allocations-filter-branch">
                  <SelectValue placeholder="All branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All branches</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name || b.username || b.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Allocation Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="allocations-filter-status">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  <SelectItem value="full">Fully allocated</SelectItem>
                  <SelectItem value="partial">Partially allocated</SelectItem>
                  <SelectItem value="shortfall">Shortfall</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            {hasAnyFilter && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                className="rounded-full h-8 text-xs"
                data-testid="allocations-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <Button
              onClick={applyFilters}
              disabled={loading}
              className="rounded-full h-8 text-xs bg-[#0078D4] hover:bg-[#106EBE] text-white"
              data-testid="allocations-apply-filters"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Filter className="w-3.5 h-3.5 mr-1" />}
              Apply
            </Button>
          </div>
        </div>

        {/* Mobile card list — Feb 2026 native-app pass */}
        <div className="md:hidden space-y-2" data-testid="allocations-list-mobile">
          {loading && rows.length === 0 && (
            <div className="p-6 text-center text-neutral-500 text-sm" data-testid="allocations-loading-mobile">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              Loading allocation report…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center text-neutral-500 text-sm" data-testid="allocations-empty-mobile">
              No allocations match the current filters.
            </div>
          )}
          {rows.map((r, idx) => (
            <MobileListCard
              key={`m-${r.order_id}-${idx}`}
              testId={`allocations-card-${idx}`}
              title={
                <span className="font-mono">
                  #{r.order_number}
                  {r.plan_may_be_stale && (
                    <Clock className="inline w-3 h-3 ml-1.5 text-amber-600" title="Plan may be stale" />
                  )}
                </span>
              }
              statusPill={
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.allocation_status === 'shortfall' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
                  {r.allocation_status || 'ok'}
                </span>
              }
              businessName={r.party_label}
              customerName={r.item_code ? `#${r.item_code}` : '—'}
              amount={`Alloc ${r.allocated_qty || 0}`}
              date={r.location === 'Unassigned' ? 'Unassigned' : r.location}
            >
              <div className="text-[11px] text-neutral-500">
                {r.item_name || '—'} · Pallet {r.pallet_id || 'Unassigned'}
              </div>
            </MobileListCard>
          ))}
        </div>

        {/* Desktop table — hidden on <md */}
        <div className="hidden md:block bg-white rounded-xl border border-neutral-200 overflow-hidden">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 border-b border-red-200 flex items-center gap-2" data-testid="allocations-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="allocations-table">
              <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Order #</th>
                  {/* Oct 2026 — Persistent `#` (line_no) column so the
                      warehouse team can match each row to the exact
                      ordered line on the paper picklist. */}
                  <th className="text-right px-2 py-2 w-10">#</th>
                  <th className="text-left px-3 py-2">Branch / Customer</th>
                  <th className="text-left px-3 py-2">Item Code</th>
                  <th className="text-left px-3 py-2">Item Name</th>
                  <th className="text-left px-3 py-2">Pallet</th>
                  <th className="text-left px-3 py-2">Location</th>
                  <th className="text-right px-3 py-2">Allocated Qty</th>
                  <th className="text-right px-3 py-2">On-hand at Alloc</th>
                  <th className="text-right px-3 py-2">Remaining After</th>
                  <th className="text-right px-3 py-2">Shortfall</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center py-6 text-neutral-500" data-testid="allocations-loading">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      Loading allocation report…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center py-8 text-neutral-500" data-testid="allocations-empty">
                      No allocations match the current filters.
                    </td>
                  </tr>
                )}
                {rows.map((r, idx) => (
                  <tr
                    key={`${r.order_id}-${idx}`}
                    className={`border-t border-neutral-100 hover:bg-neutral-50 ${r.allocation_status === 'shortfall' ? 'bg-red-50/40' : ''}`}
                    data-testid={`allocations-row-${idx}`}
                  >
                    <td className="px-3 py-1.5 font-mono text-[#0F172A]">
                      {r.order_number}
                      {r.plan_may_be_stale && (
                        <span
                          className="inline-flex items-center ml-1.5 text-amber-600"
                          title={`Plan generated ${r.generated_at || 'earlier'} — stock has moved since. Recalculate this order for current figures.`}
                          data-testid={`allocations-stale-${idx}`}
                        >
                          <Clock className="w-3 h-3" />
                        </span>
                      )}
                    </td>
                    <td
                      className="px-2 py-1.5 text-right tabular-nums text-neutral-500 font-semibold"
                      data-testid={`allocations-line-no-${idx}`}
                    >
                      {(r.line_no != null && r.line_no !== '') ? r.line_no : '—'}
                    </td>
                    <td className="px-3 py-1.5">{r.party_label}</td>
                    <td className={`px-3 py-1.5 font-mono ${r.item_code ? 'text-[#0F172A]' : 'text-neutral-400 italic'}`}>
                      {r.item_code || '—'}
                    </td>
                    <td className="px-3 py-1.5 font-medium">{r.item_name || '—'}</td>
                    <td className={`px-3 py-1.5 font-mono ${r.pallet_id ? '' : 'text-neutral-400 italic'}`}>
                      {r.pallet_id || 'Unassigned'}
                    </td>
                    <td className={`px-3 py-1.5 ${r.location === 'Unassigned' ? 'text-neutral-400 italic' : 'text-neutral-700'}`}>
                      {r.location}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{r.allocated_qty || 0}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-neutral-700">
                      {r.on_hand_at_alloc == null ? <span className="text-neutral-400">—</span> : r.on_hand_at_alloc}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-neutral-700">
                      {r.remaining_after == null ? <span className="text-neutral-400">—</span> : r.remaining_after}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {(r.shortfall_qty || 0) > 0 ? (
                        <span className="inline-flex items-center gap-0.5 text-red-700 font-bold">
                          <AlertTriangle className="w-3 h-3" /> {r.shortfall_qty}
                        </span>
                      ) : (
                        <span className="text-neutral-400">0</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusPill(r.allocation_status)}`} data-testid={`allocations-status-${idx}`}>
                        {r.allocation_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const SummaryTile = ({ label, value, tone, testid }) => {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  }[tone] || 'bg-white border-neutral-200 text-[#0F172A]';
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value ?? 0}</div>
    </div>
  );
};

export default AdminAllocations;
